/**
 * Usage without sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop2x', 'landscape-crop']}
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w" />
 *
 * Usage with sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop2x', 'landscape-crop']}
 *     sizes="(max-width: 600px) 100vw, 50vw"
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w"
 *     sizes="(max-width: 600px) 100vw, 50vw" />
 *
 *   // This means that below 600px image will take as many pixels there are available on current
 *   // viewport width (100vw) - and above that image will only take 50% of the page width.
 *   // Browser decides which image it will fetch based on current screen size.
 *
 * NOTE: for all the possible image variant names and their respective
 * sizes, see the API documentation.
 */

import Box from '@mui/material/Box';
import classNames from 'classnames';
import React from 'react';

import { lazyLoadWithDimensions } from '../../../util/contextHelpers';
import Image from '../Image';
import noImageJpg from './images/no-image.jpg';
import noImageWebp from './images/no-image.jpg.webp';

const LazyImage = lazyLoadWithDimensions(Image);

const rootSx = {
  width: '100%',
};

const noImageContainerSx = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: theme.tokens.matterColorNegative,
  position: 'relative',
});

// Mirror the Sharetribe pattern: the base root style applies only when no rootClassName
// override is passed, while the no-image-container style always applies in this branch.
const noImageContainerWithRootSx = (theme) => ({
  ...rootSx,
  ...noImageContainerSx(theme),
});

const noImageWrapperSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '225px',
};

const ResponsiveImage = ({ className = null, rootClassName = null, alt, image = null, variants, ...rest }) => {
  // Base root style applies only when no rootClassName override is passed; className is always appended.
  const rootSxProp = rootClassName ? undefined : rootSx;

  if (image == null || variants.length === 0) {
    // Base root style applies only when no rootClassName override is passed; the no-image
    // container style is always part of this branch, and className is always appended.
    const noImageSxProp = rootClassName ? noImageContainerSx : noImageContainerWithRootSx;

    return (
      <Box className={classNames(rootClassName, className) || undefined} sx={noImageSxProp}>
        <Box sx={noImageWrapperSx}>
          <Box
            className={classNames(rootClassName, className) || undefined}
            component={LazyImage}
            sources={[noImageJpg, noImageWebp]}
            sx={rootSxProp}
          />
        </Box>
      </Box>
    );
  }

  const imageVariants = image.attributes.variants;

  const srcSet = variants
    .map((variantName) => {
      const variant = imageVariants[variantName];

      if (!variant) {
        // Variant not available (most like just not loaded yet)
        return null;
      }
      // `${variant.url} ${variant.width}w`
      return `${variant.url} ${variant.width}w`;
    })
    .filter((v) => v != null)
    .join(', ');

  return (
    <Box
      alt={alt}
      className={classNames(rootClassName, className) || undefined}
      component="img"
      srcSet={srcSet}
      sx={rootSxProp}
      {...rest}
    />
  );
};

export default ResponsiveImage;
